import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { DateTime } from 'luxon';
import { finalize, take } from 'rxjs';
import { UserSalaryHistoryModel } from 'app/models/user_salary_history.model';
import { UserSalaryHistoryService } from 'app/services/user_salary_history.service';
import { ToastService } from 'app/shared/services/toast.service';
import { ApiService } from 'app/shared/apis/api.service';
import { UserModel } from 'app/models/user.model';

export type UserSalaryHistoryFormDialogData = {
    item?: UserSalaryHistoryModel | null;
    user_id: number;
};

type SalaryForm = FormGroup<{
    role_title: FormControl<string | null>;
    salary_brl: FormControl<string | number | null>;
    start_date: FormControl<DateTime | Date | string | null>;
    end_date: FormControl<DateTime | Date | string | null>;
    observation: FormControl<string | null>;
}>;

@Component({
    selector: 'app-user-salary-history-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatInputModule,
        MatIconModule,
        MatDatepickerModule,
        MatProgressSpinnerModule,
        NgxMaskDirective,
    ],
    providers: [provideNgxMask()],
    templateUrl: './user-salary-history-form.component.html',
})
export class UserSalaryHistoryFormComponent implements OnInit {
    form: SalaryForm = this.fb.group({
        role_title: [null as string | null, Validators.required],
        salary_brl: [null as string | number | null, Validators.required],
        start_date: [null as DateTime | Date | string | null, Validators.required],
        end_date: [null as DateTime | Date | string | null],
        observation: [null as string | null],
    });

    loading = false;
    loggedUser: UserModel | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: UserSalaryHistoryFormDialogData,
        private dialogRef: MatDialogRef<UserSalaryHistoryFormComponent>,
        private fb: FormBuilder,
        private service: UserSalaryHistoryService,
        private toast: ToastService,
    ) {
        this.dialogRef.addPanelClass('card-dialog');
    }

    ngOnInit(): void {
        ApiService.userCurrent.pipe(take(1)).subscribe((u) => (this.loggedUser = u));
        if (this.data?.item?.user_salary_history_id) {
            this.form.patchValue({
                role_title: this.data.item.role_title,
                salary_brl: this.data.item.salary_brl,
                start_date: this.toDateTime(this.data.item.start_date),
                end_date: this.toDateTime(this.data.item.end_date),
                observation: this.data.item.observation,
            });
        }
    }

    get isEdit(): boolean {
        return !!this.data?.item?.user_salary_history_id;
    }

    private toDateTime(v: Date | string | null | undefined): DateTime | null {
        if (!v) return null;
        if (DateTime.isDateTime(v)) return v as DateTime;
        if (v instanceof Date) return DateTime.fromJSDate(v);
        if (typeof v === 'string') {
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
                return DateTime.fromFormat(v, 'dd/MM/yyyy');
            }
            const iso = DateTime.fromISO(v.slice(0, 10));
            return iso.isValid ? iso : null;
        }
        return null;
    }

    private toIsoDate(v: DateTime | Date | string | null): string | null {
        if (!v) return null;
        if (typeof v === 'string') {
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(v.trim())) {
                const dt = DateTime.fromFormat(v.trim(), 'dd/MM/yyyy');
                return dt.isValid ? dt.toISODate() : null;
            }
            if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
            return null;
        }
        if (DateTime.isDateTime(v)) return v.isValid ? v.toISODate() : null;
        if (v instanceof Date) {
            return DateTime.fromJSDate(v).toISODate();
        }
        return null;
    }

    private parseSalary(v: string | number | null): number {
        if (v == null || v === '') return NaN;
        if (typeof v === 'number') return v;
        return Number(String(v).replace(/\./g, '').replace(',', '.'));
    }

    clear(): void {
        this.dialogRef.close(false);
    }

    done(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const value = this.form.getRawValue();
        const startIso = this.toIsoDate(value.start_date);
        const endIso = value.end_date ? this.toIsoDate(value.end_date) : null;
        if (!startIso) {
            this.toast.error('Data início inválida.');
            return;
        }
        if (value.end_date && !endIso) {
            this.toast.error('Data fim inválida.');
            return;
        }

        const salary = this.parseSalary(value.salary_brl);
        if (!Number.isFinite(salary)) {
            this.toast.error('Salário inválido.');
            return;
        }

        this.loading = true;
        const payload: any = {
            user_id: this.data.user_id,
            role_title: value.role_title,
            salary_brl: salary,
            start_date: startIso,
            end_date: endIso,
            observation: value.observation,
            created_by_user_id: this.loggedUser?.user_id ?? null,
        };

        const req$ = this.isEdit
            ? this.service.update({ ...this.data.item, ...payload } as any)
            : this.service.create(payload);

        req$.pipe(take(1), finalize(() => (this.loading = false))).subscribe({
            next: () => {
                this.toast.success(this.isEdit ? 'Registro atualizado.' : 'Registro criado.');
                this.dialogRef.close(true);
            },
            error: (err) => this.toast.error(err?.error?.message || 'Erro ao salvar.'),
        });
    }
}
